import React, { useReducer } from 'react';

/**
 * This context exists as a way to keep track of if
 * the user is trying to open a patient dialog via
 * URL. It is tightly coupled with the <ActiveDialogsProvider/>,
 * but separated out to reduce the amount of code for that
 * provider component.
 */
const UrlStateContext = React.createContext({
  isViewingPatientFromUrl: true,
  isBackForwardButtonPressed: false
});
const UrlStateUpdaterContext = React.createContext(null);

interface UrlState {
  isViewingPatientFromUrl: boolean;
  isBackForwardButtonPressed: boolean;
}

export enum UrlStateActionType {
  SetToFalse = 'SET_TO_FALSE',
  SetIsOpenViaUrl = 'SET_IS_OPEN_VIA_URL',
  SetIsBackForwardButtonPressed = 'SET_IS_BACK_FORWARD_BUTTON_PRESSED'
}

interface UrlAction {
  type: UrlStateActionType;
  payload?: boolean;
}

const urlStateReducer = (state: UrlState, action: UrlAction): UrlState => {
  switch (action.type) {
    case UrlStateActionType.SetToFalse: {
      const newState = {
        isViewingPatientFromUrl: false,
        isBackForwardButtonPressed: false
      };
      return newState;
    }
    case UrlStateActionType.SetIsOpenViaUrl: {
      const newState = {
        ...state,
        isViewingPatientFromUrl: action.payload
      };
      return newState;
    }
    case UrlStateActionType.SetIsBackForwardButtonPressed: {
      const newState = {
        ...state,
        isBackForwardButtonPressed: action.payload
      };
      return newState;
    }
    default:
      return state;
  }
};

export const UrlStateProvider: React.FC = ({ children }) => {
  // Check if initial URL contains patientId
  const initialHasPatientId =
    typeof window !== 'undefined' &&
    window.location.search.includes('patientId');
  const initialUrlState: UrlState = {
    isViewingPatientFromUrl: initialHasPatientId,
    isBackForwardButtonPressed: false
  };
  const [urlState, dispatch] = useReducer(urlStateReducer, initialUrlState);
  /**
   * We gain performance improvements by splitting out
   * the dispatch from the url state. This way there will be less
   * unnecessary re-renders, especially for components that
   * only depend on the dispatch function.
   */
  return (
    <UrlStateContext.Provider value={urlState}>
      <UrlStateUpdaterContext.Provider value={dispatch}>
        {children}
      </UrlStateUpdaterContext.Provider>
    </UrlStateContext.Provider>
  );
};

export function useUrlState(): UrlState {
  const urlState = React.useContext(UrlStateContext);
  if (!urlState) {
    throw new Error('useUrlState must be used within a UrlStateProvider');
  }
  return urlState;
}

export function useUrlStateUpdater(): React.Dispatch<UrlAction> {
  const dispatch = React.useContext(UrlStateUpdaterContext);
  if (!dispatch) {
    throw new Error(
      'useUrlStateUpdater must be used within a UrlStateProvider'
    );
  }
  return dispatch;
}
